import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation, showYesNo } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { LineUpId, LineUpResponse, CompactLineUpResponse } from '@business/entities/lineUp';
import { LineUpAPI } from '@business/api/lineUp';
import { CourseId } from '@business/entities/course';
import { UserId } from '@business/entities/account';
import { R } from '@core/utils/r';

export class LineUpBloc extends BaseBloc {
  private readonly lineupRepo = new Repository<LineUpResponse | CompactLineUpResponse>({
    getItemId: lineup => lineup.id,
  });

  readonly items$ = this.lineupRepo.items$;
  readonly selectItem = this.lineupRepo.selectItem;

  onReset() {
    this.lineupRepo.reset();
  }

  fetchLineUps = () => {
    return LineUpAPI.fetchLineups().pipe(this.lineupRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_lineups_error') }));
  };

  fetchLineUp = (id: LineUpId) => {
    return LineUpAPI.fetchLineup(id).pipe(
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_lineups_error') }),
    );
  };

  createLineUp = (task: Partial<CompactLineUpResponse>) => {
    return LineUpAPI.createLineup(task).pipe(
      this.lineupRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_lineup'),
      }),
    );
  };

  updateLineUp = (id: LineUpId, data: Partial<CompactLineUpResponse>) => {
    return LineUpAPI.updateLineup(id, data).pipe(
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_lineup'),
      }),
    );
  };

  deleteLineUp = (id: LineUpId) => {
    return showDeleteConfirmation('Delete Line-up', 'Do you really want to remove this Line-up?').pipe(
      switchMap(() => LineUpAPI.deleteLineup(id)),
      this.lineupRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_lineup'),
      }),
    );
  };

  addCourse = (lineUpId: LineUpId, courseIds: CourseId[]) => {
    return LineUpAPI.addCourse(lineUpId, courseIds).pipe(
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_lineup'),
      }),
    );
  };

  enroll = (courseId: CourseId, uids: UserId[]) => {
    return LineUpAPI.enroll(courseId, uids).pipe(
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  restart = (courseId: CourseId, uid: UserId) => {
    return LineUpAPI.restart(courseId, uid).pipe(
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  unenroll = (courseId: CourseId, uid: UserId) => {
    return showYesNo('Unenroll User', 'Do you want to unenroll this user immediately or after completing the ongoing course?', {
      btnYesLabel: 'Immediately',
      btnNoLabel: 'Complete course',
    }).pipe(
      switchMap(deleteType => {
        return LineUpAPI.unenroll(courseId, uid, deleteType);
      }),
      //   switchMap(() => LineUpAPI.unenroll(courseId, uid)),
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('create_course_section'),
      }),
    );
  };

  sortCourses = (lineUpId: LineUpId, courseIds: CourseId[]) => {
    return LineUpAPI.sortCourses(lineUpId, courseIds).pipe(
      tap(() => {
        const lineUp = this.lineupRepo.getItem(lineUpId)! as LineUpResponse;
        const newCourses = lineUp.courses.map(c => {
          return { ...c, menuOrder: R.findIndex(courseIds, i => i === c.id) };
        });

        this.lineupRepo.updateOne({
          id: lineUpId,
          changes: () => ({ courses: R.sortBy(newCourses, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  removeCourse = (lineUpId: LineUpId, courseId: CourseId) => {
    return showDeleteConfirmation('Remove Course', 'Do you really want to remove this course?').pipe(
      switchMap(() => LineUpAPI.removeCourse(lineUpId, courseId)),
      this.lineupRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('remove_course'),
      }),
    );
  };
}
